import { useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Subscription/Pro state for the current user.
 * - isPro: true if active subscription, trialing, or admin
 * - status: 'free' | 'trialing' | 'active' | 'expired'
 * - canStartTrial: user never used their 30-day trial yet
 *
 * `startTrial` activates the 30-day trial DIRECTLY in the DB (no Stripe, no card).
 * `startPaidCheckout(plan)` opens Stripe Checkout with plan="monthly" (€4,99)
 *   or plan="yearly" (€40).
 * `cancelSubscription()` schedules a cancel-at-period-end on Stripe (user keeps
 *   access until the natural end date).
 */
export function useSubscription() {
  const { user, refreshUser } = useAuth();

  const isPro = !!user?.is_pro;
  const status = user?.subscription_status || "free";
  const canStartTrial = !!user && !user.trial_used && !isPro;

  const startTrial = useCallback(async () => {
    if (user && user.trial_used) {
      window.location.href = "/billing";
      return null;
    }
    try {
      const { data } = await api.post("/subscription/start-trial");
      toast.success("Prova attivata! 30 giorni gratis, senza carta.");
      if (typeof refreshUser === "function") {
        try { await refreshUser(); } catch { /* non-fatal */ }
      }
      return data;
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(msg || "Impossibile attivare la prova. Riprova.");
      return null;
    }
  }, [user, refreshUser]);

  const startPaidCheckout = useCallback(async (plan = "monthly") => {
    try {
      const { data } = await api.post("/billing/checkout", {
        origin_url: window.location.origin,
        plan,
      });
      window.location.href = data.url;
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile avviare il pagamento");
      return null;
    }
  }, []);

  const cancelSubscription = useCallback(async () => {
    try {
      const { data } = await api.post("/subscription/cancel");
      toast.success("Disdetta programmata · manterrai Pro fino alla scadenza");
      if (typeof refreshUser === "function") {
        try { await refreshUser(); } catch { /* non-fatal */ }
      }
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile disdire ora");
      return null;
    }
  }, [refreshUser]);

  const resumeSubscription = useCallback(async () => {
    try {
      await api.post("/subscription/resume");
      toast.success("Rinnovo automatico riattivato");
      if (typeof refreshUser === "function") {
        try { await refreshUser(); } catch { /* non-fatal */ }
      }
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile ripristinare");
      return false;
    }
  }, [refreshUser]);

  return {
    isPro, status, canStartTrial,
    startTrial, startPaidCheckout, cancelSubscription, resumeSubscription,
  };
}
